import { useMutation, useQueries, useQuery } from "@tanstack/react-query";
import { successMessage } from "../helpers/ErrorHandler";
import { PaginationResult } from "../types/Pagination";
import { ApiServiceErr, MutOptions, axiosApi } from "./apiService";

interface Products {
  _id: string;
  product_name: string;
}

interface Ingredients {
  _id: string;
  ingredient_name: string;
  productIds: string[];
}

interface SupplierIngredientDetails {
  _id: string;
  supplier_id: string;
  ingredient_id: string;
  data_type: string;
}

export interface Supplier {
  _id: string;
  supplier_name: string;
  supplier_contact_name: string;
  supplier_email: string;
  supplier_phone: string;
  products: Products[];
  ingredients: Ingredients[];
  is_supplier_as_user: boolean;
  supplierIngredientDetails: SupplierIngredientDetails[];
  supplier_type: number;
}

interface PaginationParams {
  pageNumber: number;
  pageSize: number;
  searchKey?: string;
  is_supplier_as_user?: boolean;
  supplier_type?: number[];
  orderBy?: string;
}

export const useGetSuppliers = ({
  pageNumber,
  pageSize,
  searchKey,
  is_supplier_as_user,
  supplier_type,
  orderBy
}: PaginationParams) =>
  useQuery<PaginationResult<Supplier[]>, ApiServiceErr>(
    [
      "GetSuppliersWithPagination",
      pageNumber,
      pageSize,
      searchKey,
      is_supplier_as_user,
      supplier_type,
      orderBy
    ],
    async () => {
      const response = await axiosApi.post("/supplier/list", {
        pageNumber,
        pageSize,
        searchKey,
        is_supplier_as_user,
        supplier_type,
        orderBy
      });
      return response.data;
    }
  );

export const useUpdateSupplierDetails = (opt?: MutOptions<string>) =>
  useMutation<string, ApiServiceErr, any>(async data => {
    const response = await axiosApi.put(`/supplier/${data._id}`, data);
    successMessage("Supplier updated successfully");
    return response.data;
  }, opt);

export const useGetSupplierById = (id: string | null) =>
  useQuery<Supplier>(
    ["getSupplierId", id],
    async () => {
      const response = await axiosApi.get(`/supplier/${id}`);
      return response.data;
    },
    {
      enabled: !!id
    }
  );

export const useGetSuppliersByIds = (supplierIds: string[]) => {
  return useQueries({
    queries: supplierIds.map(
      id => {
        return {
          queryKey: ["getSupplierId", id],
          queryFn: async () => {
            const response = await axiosApi.get(`/supplier/${id}`);
            return response.data;
          }
        };
      },
      { enabled: !!supplierIds }
    )
  });
};

export const useCreateSupplierDrawingRequest = (opt?: MutOptions<string>) =>
  useMutation<string, ApiServiceErr, any>(async data => {
    const response = await axiosApi.post("/supplier-drawing-request", data);
    successMessage("Supplier drawing request sent successfully");
    return response.data;
  }, opt);

export const useGetSupplierRequest = (pageNumber: number, pageLimit: number, searchKey: string = "") =>
  useQuery<PaginationResult<any[]>, ApiServiceErr>(
    ["GetSuppliersRequestWithPagination", pageNumber, pageLimit, searchKey],
    async () => {

      const response = await axiosApi.get(
        "/supplier-drawing-request/sent-request",
        {
          params: {
            pageNumber,
            pageLimit,
            searchKey,
          },
      });
      return response.data;
    }
  );

export const useDeleteSupplierById = () =>
  useMutation<any, ApiServiceErr, string>(
    async (supplierId: string) => {
      const response = await axiosApi.delete(`/supplier/${supplierId}`);
      return response.data;
    },
    {
      onSuccess: () => {
        successMessage("Supplier delete successfully");
      }
    }
  );
